import type {
  PostingPreview,
  PostingPreviewInput,
} from '@/routes/Agency/PaySurge/api';
import type { PostingSurgeItem } from '@/routes/Agency/PaySurge/components/PostingSurgeDialog';

/*
 * GRAVY-10881 — "Place new order" warns when a shift it posts will surge the
 * moment it goes live. These two functions translate between the order
 * payload and graphy's posting preview; the decision itself (when each shift
 * posts, which tier the sweep applies) is graphy's, so none of it lives here.
 *
 * `buildCreatePayload` emits one job per shift, so a payload job IS a shift,
 * and its index is the key the preview echoes back.
 */

type PayloadJob = {
  skill_id?: number | null;
  visibility?: string;
  publish?: boolean;
  quantity?: number;
  hire_workers?: unknown[];
  is_holiday?: boolean;
  shifts?: { start_at?: string; time_zone_offset?: number }[];
};

const jobsOf = (payload: { jobs?: unknown[] }): PayloadJob[] =>
  (payload.jobs ?? []) as PayloadJob[];

/** The preview's request: one entry per shift that has a start. */
export const postingPreviewInputs = (payload: {
  jobs?: unknown[];
}): PostingPreviewInput[] =>
  jobsOf(payload).flatMap((job, index) => {
    const start = job.shifts?.[0]?.start_at;
    if (!start) return [];
    return [
      {
        key: index,
        first_shift_start_at: start,
        visibility: job.visibility === 'PUBLIC' ? 'PUBLIC' : 'PRIVATE',
        publish: job.publish === true,
        quantity: job.quantity ?? 1,
        hire_count: job.hire_workers?.length ?? 0,
      },
    ];
  });

/*
 * The shift's start as the admin entered it — in the location's time, which
 * the payload carries as an hour offset — not the browser's.
 */
const wallTime = (iso: string, offsetHours: number | undefined): string => {
  const at = new Date(new Date(iso).getTime() + (offsetHours ?? 0) * 3_600_000);
  return at.toLocaleString('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/** The dialog's rows for the shifts the preview says surge at once. */
export const postingSurgeItems = (
  preview: PostingPreview,
  payload: { jobs?: unknown[] },
  skills: { id: number; name?: string | null }[] = [],
): PostingSurgeItem[] => {
  const jobs = jobsOf(payload);
  return preview.jobs.map((surge, i) => {
    const job = jobs[Number(surge.key)];
    const skill = skills.find((s) => s.id === job?.skill_id)?.name;
    const when = wallTime(
      surge.first_shift_start_at,
      job?.shifts?.[0]?.time_zone_offset,
    );
    return {
      key: surge.key ?? i,
      label: skill ? `${skill} · ${when}` : when,
      startsAt: surge.first_shift_start_at,
      postsAt: surge.posts_at,
      postsNow: surge.posts_now,
      leadTimeMinutes: surge.lead_time_minutes,
      rateCents: surge.rate_cents,
      isHoliday: job?.is_holiday === true,
    };
  });
};
